// MiniPattern → drum grid state.
//
// "Gridable" means the pattern's time axis can be rendered as a uniform
// row of N step columns where each cell is either lit (one or more
// sample/note hits) or empty (rest). The grid is a *derived view* over
// the structured AST — round-trip fidelity is enforced by the
// drumGridToMini adapter and its tests.
//
// Supported shapes:
//   1. A flat list of step-contents:        bd hh ~ cp
//   2. An implicit single-group wrapper:    [bd hh ~ cp]
//   3. A single repeat-of-atom expansion:   bd*4 → 4 cells of bd
//   4. Step-contents are atoms, rests, or parallel-of-atoms (multi-hit
//      on the same step like [bd,sd]).
//
// Anything else (mixed repeats, nested groups, polymeter, euclid,
// choice, slow, weight, bang, etc.) is reported as "not gridable" so
// the UI can fall back to source view rather than risking silent edits.

import type { MiniNode, MiniPattern, Numeric } from '../types';

export type GridRow = {
  /** Sample name ("bd", "hh:2") or pitch name ("c4", "eb3"). */
  name: string;
  /** Lit-state per step. Length === steps. */
  cells: boolean[];
};

export type DrumGridState = {
  steps: number;
  /** Rows in display order — typically the order in which sample names
   *  first appear in the pattern. */
  rows: GridRow[];
};

export type FromMiniResult =
  | { ok: true; state: DrumGridState }
  | { ok: false; reason: string };

export function drumGridFromMini(pattern: MiniPattern): FromMiniResult {
  if (!pattern || pattern.length === 0) {
    return { ok: false, reason: 'pattern is empty' };
  }

  const stepNodes = unwrapToSteps(pattern);
  if (!stepNodes) {
    return {
      ok: false,
      reason: 'pattern is too complex for grid view (nested / polymeter / euclid / etc.)',
    };
  }

  // Each step is now a list of atom names lit at that step. Empty list
  // = rest. Build the row set in first-appearance order.
  const rowOrder: string[] = [];
  const rowIndex = new Map<string, number>();
  const litPerStep: string[][] = [];
  for (const node of stepNodes) {
    const lit = stepNames(node);
    if (lit === null) {
      return {
        ok: false,
        reason: `step contains a node kind that the grid can't represent (${node.kind})`,
      };
    }
    for (const name of lit) {
      if (!rowIndex.has(name)) {
        rowIndex.set(name, rowOrder.length);
        rowOrder.push(name);
      }
    }
    litPerStep.push(lit);
  }

  const rows: GridRow[] = rowOrder.map((name) => ({
    name,
    cells: new Array(stepNodes.length).fill(false),
  }));
  for (let s = 0; s < stepNodes.length; s++) {
    for (const name of litPerStep[s]!) {
      rows[rowIndex.get(name)!]!.cells[s] = true;
    }
  }

  return { ok: true, state: { steps: stepNodes.length, rows } };
}

// Resolve the pattern down to a flat list of step nodes — or null when
// the structure isn't a uniform step grid.
function unwrapToSteps(pattern: MiniPattern): MiniNode[] | null {
  // Single-element pattern that's a repeat: bd*4 → 4 atoms of bd. Strudel
  // treats top-level repeats as cell expansion, so this is honest.
  if (pattern.length === 1) {
    const only = pattern[0]!;
    if (only.kind === 'repeat') {
      const n = numericValue(only.count);
      if (n === null || n < 1) return null;
      const inner = only.inner;
      // Only expand atoms / rests / parallels — anything more complex
      // means time subdivision inside the repeat, which the grid can't
      // honestly represent.
      if (
        inner.kind !== 'atom' &&
        inner.kind !== 'rest' &&
        inner.kind !== 'parallel'
      ) {
        return null;
      }
      // Note: every entry is the same `inner` reference. Safe because
      // the only consumer (stepNames) reads kind / name / index — never
      // mutates. If a caller ever mutates one of these atoms it would
      // mutate all N entries; revisit then.
      const expanded: MiniNode[] = [];
      for (let i = 0; i < n; i++) expanded.push(inner);
      return expanded;
    }
    if (only.kind === 'group') {
      // Implicit-group wrap: [bd hh cp ~] is the same time axis as
      // bd hh cp ~ at the top level.
      return only.children;
    }
  }
  return pattern;
}

// What samples/notes are lit at a given step? Returns null if the node
// can't be represented as a step. Empty array = explicit rest.
function stepNames(node: MiniNode): string[] | null {
  if (node.kind === 'atom') return [atomDisplayName(node)];
  if (node.kind === 'rest') return [];
  if (node.kind === 'parallel') {
    // [bd,sd] — multi-hit on one step. Every child must itself be a
    // simple atom; a parallel-of-parallel or parallel-of-group is
    // beyond what the grid can faithfully edit.
    const out: string[] = [];
    for (const child of node.children) {
      if (child.kind !== 'atom') return null;
      out.push(atomDisplayName(child));
    }
    return out;
  }
  return null;
}

// Atoms split a name like "bd:2" into {name: 'bd', index: 2}. The grid
// row name needs the round-tripable form, so reattach the colon-index.
export function atomDisplayName(atom: { name: string; index?: number }): string {
  return atom.index !== undefined ? `${atom.name}:${atom.index}` : atom.name;
}

// Pull a literal number out of a Numeric node; null if it's an
// expression / ref / modulator. Used for repeat counts.
function numericValue(n: Numeric): number | null {
  return n.kind === 'num' ? n.value : null;
}
